// AT-2 (2026-06-04) — Editor de Atestado Técnico em modal full-width.
// Tabs internas: Geral / Escopo / Atividades / Equipe / Equipamentos / Quantitativo.
// Edita o JSONB de cada bloco com componentes leves (lista editável).
// Pré-popula equipe técnica a partir das ARTs do contrato (botão "Importar de ARTs").

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Modal, Tabs, Form, Input, DatePicker, Button, Space, Typography,
  message, Spin, Tag, Card, Divider, Flex, Tooltip, Empty, InputNumber,
} from 'antd';
import {
  SaveOutlined, DeleteOutlined, PlusOutlined, ImportOutlined, RobotOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from './utils';
import ModalSugerirAtestado from './ModalSugerirAtestado';
import { statusCfg } from './atestadoStatus';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Atestado {
  id: string;
  numero: string | null;
  status: string;
  // AT-8 (Notion import)
  titulo?: string | null;
  tags?: string[] | null;
  revisor?: string | null;
  protocolo_crea?: string | null;
  anexo_minuta_s3_key?: string | null;
  anexo_minuta_nome?: string | null;
  anexo_assinado_s3_key?: string | null;
  anexo_assinado_nome?: string | null;
  anexo_timbrado_s3_key?: string | null;
  anexo_timbrado_nome?: string | null;
  cat_anexo_s3_key?: string | null;
  anexos_extras?: Array<{ categoria: string; nome: string; s3_key: string }> | null;
  pdf_final_s3_key?: string | null;
  data_emissao: string | null;
  local_emissao: string | null;
  representante_legal_nome: string | null;
  representante_legal_cpf: string | null;
  objeto_descricao: string | null;
  escopo_descritivo: string | null;
  atividades_agrupadas: Array<{ titulo: string; itens: string[] }> | null;
  equipamentos_softwares: Array<{ atividade: string; recursos: string[] }> | null;
  equipe_tecnica: Array<{ nome: string; formacao: string; funcao: string; registro: string; periodo: string }> | null;
  quantitativo: Array<{ descricao: string; unidade: string; quantidade: number | string }> | null;
  observacoes: string | null;
  cat_numero: string | null;
  cat_data_emissao: string | null;
  contrato_numero?: string;
  contrato_titulo?: string;
  contrato_objetivos?: string;
  contrato_data_inicio_real?: string;
  contrato_data_termino_real?: string;
  contrato_valor?: number;
  cliente_razao_social?: string;
  cliente_cnpj?: string;
}

interface Props {
  atestadoId: string;
  contrato: { id: string; numero?: string };
  onClose: () => void;
  onSaved: () => void;
}

// Funções utilitárias pra editar arrays inline
const isoToDayjs = (v: string | null) => v ? dayjs(v.slice(0, 10)) : null;

function ModalAtestadoInner({ atestadoId, contrato, onClose, onSaved }: Props) {
  const [atestado, setAtestado] = useState<Atestado | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dirty, setDirty] = useState(false);
  // AT-IA-3 (2026-06-05): modal de preencher com IA
  const [sugerirAberto, setSugerirAberto] = useState(false);
  // AT-4 (2026-06-06): geracao de PDF
  const [gerandoPdf, setGerandoPdf] = useState(false);
  // ATM-T1 fix (2026-06-09): URL do PDF gerado pra mostrar em modal
  // (evita window.open que e bloqueado por Safari mobile)
  const [pdfUrlGerada, setPdfUrlGerada] = useState<string | null>(null);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_obter', id: atestadoId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAtestado(d);
      setDirty(false);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [atestadoId]);

  function setCampo<K extends keyof Atestado>(k: K, v: Atestado[K]) {
    if (!atestado) return;
    setAtestado({ ...atestado, [k]: v });
    setDirty(true);
  }

  async function salvar() {
    if (!atestado || !dirty) return;
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = { action: 'op_atestado_atualizar', id: atestado.id };
      for (const k of ['data_emissao','local_emissao','representante_legal_nome','representante_legal_cpf',
                       'objeto_descricao','escopo_descritivo','atividades_agrupadas',
                       'equipamentos_softwares','equipe_tecnica','quantitativo',
                       'observacoes','cat_numero','cat_data_emissao'] as const) {
        if (atestado[k] !== undefined) payload[k] = atestado[k];
      }
      await axios.post(`${API_URL}/comercial/propostas-geradas`, payload,
        { headers: { Authorization: token } });
      message.success('Salvo');
      setDirty(false);
      onSaved();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir() {
    if (!atestado) return;
    Modal.confirm({
      title: 'Excluir atestado?',
      content: `O atestado ${atestado.numero} será apagado.`,
      okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_atestado_excluir', id: atestado.id },
            { headers: { Authorization: token } });
          message.success('Excluído');
          onSaved();
          onClose();
        } catch (e: any) {
          message.error('Falha: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  // AT-4 (2026-06-06): gera PDF via pdf-generator
  async function gerarPdf() {
    if (!atestado) return;
    if (dirty) {
      const confirm = window.confirm(
        'Você tem alterações não salvas. O PDF vai usar o estado salvo no banco. '
        + 'Quer salvar antes de gerar?'
      );
      if (confirm) {
        await salvar();
      }
    }
    setGerandoPdf(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_gerar_pdf', id: atestado.id },
        { headers: { Authorization: token }, timeout: 60000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) {
        // Mostra modal com link clicável (evita popup blocker Safari mobile)
        setPdfUrlGerada(d.url);
      } else {
        message.warning('PDF gerado mas sem URL');
      }
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setGerandoPdf(false); }
  }

  async function importarEquipeDasArts() {
    if (!atestado) return;
    try {
      const token = await getToken();
      // Reusa endpoint de ARTs do contrato — devolve lista de ARTs c/ rt_*
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_arts', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const arts = Array.isArray(d) ? d : (d.arts || []);
      const equipe = arts.filter((a: any) => a.rt_nome).map((a: any) => ({
        nome: a.rt_nome || '',
        formacao: a.rt_titulo_profissional || '',
        funcao: 'Responsável Técnico',
        registro: a.rt_carteira || a.rt_rnp || '',
        periodo: a.inicio_art && a.fim_art
          ? `${dayjs(a.inicio_art).format('DD/MM/YYYY')} a ${dayjs(a.fim_art).format('DD/MM/YYYY')}`
          : '',
      }));
      if (equipe.length === 0) {
        message.warning('Nenhuma ART com RT preenchido');
        return;
      }
      // Merge com equipe existente — não sobrescreve nomes já cadastrados
      const existentes = atestado.equipe_tecnica || [];
      const novos = [...existentes];
      for (const e of equipe) {
        const idx = novos.findIndex((x: any) => (x.nome || '').toLowerCase() === e.nome.toLowerCase());
        if (idx >= 0) novos[idx] = { ...novos[idx], ...e };
        else novos.push(e);
      }
      setCampo('equipe_tecnica', novos);
      message.success(`${equipe.length} RT(s) importado(s) das ARTs`);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  // ATM-T3 (2026-06-09): importa gestor + coordenador do contrato (operacao.contrato_equipe)
  // cruzando com admin.usuario_dados_tecnicos pra trazer formação/CREA/RNP.
  async function importarEquipeDoContrato() {
    if (!atestado) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_importar_equipe_contrato', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const equipe = Array.isArray(d) ? d : (d.equipe || []);
      if (equipe.length === 0) {
        message.warning('Nenhum gestor/coordenador cadastrado no contrato (ou sem dados técnicos preenchidos no admin)');
        return;
      }
      // Merge com equipe existente (não duplica por nome, mas atualiza dados)
      const existentes = atestado.equipe_tecnica || [];
      const novos = [...existentes];
      let adicionados = 0, atualizados = 0;
      for (const e of equipe) {
        const idx = novos.findIndex((x: any) => (x.nome || '').toLowerCase() === (e.nome || '').toLowerCase());
        if (idx >= 0) { novos[idx] = { ...novos[idx], ...e }; atualizados++; }
        else { novos.push(e); adicionados++; }
      }
      setCampo('equipe_tecnica', novos);
      message.success(`${adicionados} adicionado(s), ${atualizados} atualizado(s)`);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  // ATM-T3 (2026-06-09): importa quantitativo das ARTs do contrato
  // (art_atividade_linha com quantidade/unidade do catálogo).
  async function importarQuantitativoDaArt() {
    if (!atestado) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_importar_quantitativo_arts', contrato_id: contrato.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const quant = Array.isArray(d) ? d : (d.quantitativo || []);
      if (quant.length === 0) {
        message.warning('Nenhuma linha de quantitativo encontrada nas ARTs do contrato');
        return;
      }
      setCampo('quantitativo', quant);
      message.success(`${quant.length} linha(s) importada(s) das ARTs`);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  if (loading || !atestado) {
    return (
      <Modal open onCancel={onClose} footer={null} width="80%" title="Atestado">
        <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>
      </Modal>
    );
  }

  // AT-8: contagem de anexos pro badge da aba
  const qtdAnexos =
    (atestado.anexo_minuta_s3_key ? 1 : 0) +
    (atestado.anexo_assinado_s3_key ? 1 : 0) +
    (atestado.anexo_timbrado_s3_key ? 1 : 0) +
    (atestado.cat_anexo_s3_key ? 1 : 0) +
    (atestado.pdf_final_s3_key ? 1 : 0) +
    (atestado.anexos_extras || []).length;

  const tabsItems = [
    {
      key: 'geral', label: 'Geral',
      children: <TabGeral atestado={atestado} setCampo={setCampo} />,
    },
    {
      key: 'escopo', label: 'Escopo',
      children: <TabEscopo atestado={atestado} setCampo={setCampo} />,
    },
    {
      key: 'atividades', label: 'Atividades',
      children: <ListaEditavel
        valor={atestado.atividades_agrupadas || []}
        onChange={(v) => setCampo('atividades_agrupadas', v as any)}
        rotuloItem="Grupo de atividades"
        camposNovo={{ titulo: '', itens: [] }}
        camposItem={[
          { key: 'titulo', label: 'Título do grupo (ex: Estudo Hidrológico)', tipo: 'text' },
          { key: 'itens', label: 'Atividades do grupo (1 por linha)', tipo: 'lista_string' },
        ]} />,
    },
    {
      key: 'equipe', label: 'Equipe técnica',
      children: <ListaEditavel
        valor={atestado.equipe_tecnica || []}
        onChange={(v) => setCampo('equipe_tecnica', v as any)}
        rotuloItem="Profissional"
        camposNovo={{ nome: '', formacao: '', funcao: '', registro: '', periodo: '' }}
        camposItem={[
          { key: 'nome', label: 'Nome', tipo: 'text' },
          { key: 'formacao', label: 'Formação', tipo: 'text' },
          { key: 'funcao', label: 'Função', tipo: 'text' },
          { key: 'registro', label: 'Registro CREA', tipo: 'text' },
          { key: 'periodo', label: 'Período', tipo: 'text' },
        ]}
        botaoExtra={<Space size={4}>
          <Button size="small" icon={<ImportOutlined />} onClick={importarEquipeDoContrato}>
            Importar do contrato
          </Button>
          <Button size="small" icon={<ImportOutlined />} onClick={importarEquipeDasArts}>
            Importar das ARTs
          </Button>
        </Space>} />,
    },
    {
      key: 'equipamentos', label: 'Equipamentos / Software',
      children: <ListaEditavel
        valor={atestado.equipamentos_softwares || []}
        onChange={(v) => setCampo('equipamentos_softwares', v as any)}
        rotuloItem="Atividade × recursos"
        camposNovo={{ atividade: '', recursos: [] }}
        camposItem={[
          { key: 'atividade', label: 'Atividade (ex: Modelagem hidrológica)', tipo: 'text' },
          { key: 'recursos', label: 'Equipamentos / Softwares (1 por linha)', tipo: 'lista_string' },
        ]} />,
    },
    {
      key: 'quantitativo', label: 'Quantitativo',
      children: <ListaEditavel
        valor={atestado.quantitativo || []}
        onChange={(v) => setCampo('quantitativo', v as any)}
        rotuloItem="Linha"
        camposNovo={{ descricao: '', unidade: '', quantidade: 0 }}
        camposItem={[
          { key: 'descricao', label: 'Descrição', tipo: 'text' },
          { key: 'unidade', label: 'Unidade', tipo: 'text' },
          { key: 'quantidade', label: 'Quantidade', tipo: 'number' },
        ]}
        botaoExtra={<Button size="small" icon={<ImportOutlined />} onClick={importarQuantitativoDaArt}>
          Importar da ART
        </Button>} />,
    },
    // AT-8 (2026-06-10): anexos + dados vindos do Notion (tags, revisor,
    // protocolo CREA, minuta, assinado, timbrado, CAT, arquivos base)
    {
      key: 'anexos',
      label: `📎 Anexos${qtdAnexos > 0 ? ` (${qtdAnexos})` : ''}`,
      children: <TabAnexos atestado={atestado} />,
    },
  ];

  return (
    <Modal open onCancel={onClose} width="85%"
      title={
        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Space size={12}>
            <Title level={5} style={{ margin: 0, color: '#020D7F' }}>
              📄 {atestado.numero || (atestado as any).titulo || 'Atestado (sem número)'}
            </Title>
            <Tag color={statusCfg(atestado.status).tagColor}>{statusCfg(atestado.status).label}</Tag>
          </Space>
          <Space size={6}>
            <Button size="small" icon={<RobotOutlined />}
              onClick={() => setSugerirAberto(true)}
              style={{ background: '#f0f4ff', borderColor: '#0930D0', color: '#020D7F' }}>
              Preencher com IA
            </Button>
            <Button size="small" icon={<FilePdfOutlined />}
              loading={gerandoPdf}
              onClick={gerarPdf}
              type="primary" danger>
              Gerar PDF
            </Button>
          </Space>
        </Flex>
      }
      footer={
        <Space>
          <Button danger icon={<DeleteOutlined />} onClick={excluir}>Excluir</Button>
          <Button onClick={onClose}>Fechar</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={salvando}
            disabled={!dirty} onClick={salvar}>
            Salvar
          </Button>
        </Space>
      }>
      <Card size="small" style={{ background: '#F0F5FF', marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Text style={{ fontSize: 11 }}>
          <strong>Cliente:</strong> {atestado.cliente_razao_social || '—'}
          {atestado.cliente_cnpj && ` (${atestado.cliente_cnpj})`}
          {' · '}
          <strong>Contrato:</strong> {atestado.contrato_numero} — {atestado.contrato_titulo}
        </Text>
      </Card>
      <Tabs items={tabsItems} size="small" />

      {/* ATM-T1 fix (2026-06-09): modal com link do PDF pra Safari mobile */}
      <Modal open={!!pdfUrlGerada} onCancel={() => setPdfUrlGerada(null)}
        title="✓ PDF gerado"
        footer={<Button onClick={() => setPdfUrlGerada(null)}>Fechar</Button>}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>O PDF do atestado <strong>{atestado.numero}</strong> foi gerado.</Text>
          <Button type="primary" size="large" icon={<FilePdfOutlined />} block
            href={pdfUrlGerada || ''} target="_blank" rel="noreferrer">
            Abrir / Baixar PDF
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>
            No mobile, mantenha pressionado pra "Abrir em nova aba" se não abrir direto.
            Link válido por 1 hora.
          </Text>
        </Space>
      </Modal>

      {/* AT-IA-3 (2026-06-05): preencher com IA */}
      <ModalSugerirAtestado
        open={sugerirAberto}
        atestadoId={atestadoId}
        contratoId={contrato.id}
        atestadoAtual={atestado}
        onCancel={() => setSugerirAberto(false)}
        onAplicar={(campos) => {
          setSugerirAberto(false);
          const novo: any = { ...atestado };
          for (const [k, v] of Object.entries(campos)) {
            novo[k] = v;
          }
          setAtestado(novo);
          setDirty(true);
          message.success(`${Object.keys(campos).length} campo(s) aplicados — revise e salve`);
        }} />
    </Modal>
  );
}

// ─── Aba Geral ─────────────────────────────────────────────────────────────
function TabGeral({ atestado, setCampo }: { atestado: Atestado; setCampo: any }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Data de emissão">
        <DatePicker value={isoToDayjs(atestado.data_emissao)} format="DD/MM/YYYY"
          onChange={(v) => setCampo('data_emissao', v ? v.format('YYYY-MM-DD') : null)}
          style={{ width: 180 }} />
      </Form.Item>
      <Form.Item label="Local de emissão (cidade do cliente normalmente)">
        <Input value={atestado.local_emissao || ''}
          onChange={(e) => setCampo('local_emissao', e.target.value || null)}
          placeholder="Ex: Passo Fundo" style={{ maxWidth: 320 }} />
      </Form.Item>
      <Divider style={{ margin: '12px 0' }} />
      <Form.Item label="Objeto do contrato (descrição longa)"
        extra="Use o botão 🤖 Preencher com IA no topo — fonte única é o PDF do contrato no Drive.">
        <TextArea rows={4} value={atestado.objeto_descricao || ''}
          onChange={(e) => setCampo('objeto_descricao', e.target.value || null)}
          placeholder="Texto descritivo do objeto…" />
      </Form.Item>
      <Divider style={{ margin: '12px 0' }} />
      <Text strong style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>
        Representante legal (lado do cliente)
      </Text>
      <Form.Item label="Nome" style={{ marginTop: 8 }}>
        <Input value={atestado.representante_legal_nome || ''}
          onChange={(e) => setCampo('representante_legal_nome', e.target.value || null)} />
      </Form.Item>
      <Form.Item label="CPF">
        <Input value={atestado.representante_legal_cpf || ''}
          onChange={(e) => setCampo('representante_legal_cpf', e.target.value || null)}
          style={{ maxWidth: 180 }} />
      </Form.Item>
      <Divider style={{ margin: '12px 0' }} />
      <Form.Item label="Observações internas">
        <TextArea rows={2} value={atestado.observacoes || ''}
          onChange={(e) => setCampo('observacoes', e.target.value || null)} />
      </Form.Item>
    </Form>
  );
}

// ─── Aba Escopo ────────────────────────────────────────────────────────────
function TabEscopo({ atestado, setCampo }: { atestado: Atestado; setCampo: any }) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Escopo técnico — descrição do que foi feito (será gerado por IA na AT-5 a partir do relatório técnico do Drive).
        Por enquanto, edite manualmente.
      </Text>
      <TextArea rows={14} style={{ marginTop: 8 }}
        value={atestado.escopo_descritivo || ''}
        onChange={(e) => setCampo('escopo_descritivo', e.target.value || null)}
        placeholder="Levantamento e análise da hidrografia e hidrologia da bacia, dimensionamento de precipitações e cheias de projeto, simulação do escoamento…" />
    </div>
  );
}

// ─── Componente reusável: lista editável de objetos ───────────────────────
type CampoTipo = 'text' | 'number' | 'lista_string';

interface CampoSpec {
  key: string;
  label: string;
  tipo: CampoTipo;
}

function ListaEditavel({ valor, onChange, rotuloItem, camposNovo, camposItem, botaoExtra }: {
  valor: any[];
  onChange: (v: any[]) => void;
  rotuloItem: string;
  camposNovo: any;
  camposItem: CampoSpec[];
  botaoExtra?: React.ReactNode;
}) {
  function adicionar() { onChange([...(valor || []), { ...camposNovo }]); }
  function remover(idx: number) { onChange((valor || []).filter((_, i) => i !== idx)); }
  function setItem(idx: number, k: string, v: any) {
    const next = [...(valor || [])];
    next[idx] = { ...next[idx], [k]: v };
    onChange(next);
  }
  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>{(valor || []).length} {rotuloItem.toLowerCase()}(s)</Text>
        <Space>
          {botaoExtra}
          <Button size="small" icon={<PlusOutlined />} onClick={adicionar}>Adicionar</Button>
        </Space>
      </Flex>
      {(valor || []).length === 0 ? (
        <Empty description={`Nenhum ${rotuloItem.toLowerCase()} ainda`} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          {(valor || []).map((item, idx) => (
            <Card size="small" key={idx} bodyStyle={{ padding: 10 }}
              extra={<Tooltip title="Remover"><Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => remover(idx)} /></Tooltip>}
              title={<Text strong style={{ fontSize: 11, color: '#666' }}>{rotuloItem} #{idx + 1}</Text>}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {camposItem.map(c => (
                  <div key={c.key}>
                    <Text style={{ fontSize: 10, color: '#888' }}>{c.label}</Text>
                    {c.tipo === 'text' && (
                      <Input size="small" value={item[c.key] || ''}
                        onChange={(e) => setItem(idx, c.key, e.target.value)} />
                    )}
                    {c.tipo === 'number' && (
                      <InputNumber size="small" value={Number(item[c.key]) || 0}
                        onChange={(v) => setItem(idx, c.key, v)} style={{ width: '100%' }} />
                    )}
                    {c.tipo === 'lista_string' && (
                      <TextArea size="small" rows={3}
                        value={(item[c.key] || []).join('\n')}
                        onChange={(e) => setItem(idx, c.key, e.target.value.split('\n').filter(s => s.trim()))}
                        placeholder="Uma linha por item" />
                    )}
                  </div>
                ))}
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}

// ─── AT-8 (2026-06-10): Aba Anexos — arquivos + dados vindos do Notion ─────
function TabAnexos({ atestado }: { atestado: Atestado }) {
  const [baixando, setBaixando] = useState<string | null>(null);

  async function baixar(tipo: string, s3Key?: string) {
    setBaixando(s3Key || tipo);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atestado_anexo_url', id: atestado.id, tipo, s3_key: s3Key },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) window.open(d.url, '_blank', 'noopener');
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setBaixando(null); }
  }

  const linhas: Array<{ rotulo: string; nome: string; tipo: string; s3_key?: string }> = [];
  if (atestado.anexo_minuta_s3_key)
    linhas.push({ rotulo: 'Minuta', nome: atestado.anexo_minuta_nome || 'arquivo', tipo: 'minuta' });
  if (atestado.anexo_assinado_s3_key)
    linhas.push({ rotulo: 'Atestado assinado', nome: atestado.anexo_assinado_nome || 'arquivo', tipo: 'assinado' });
  if (atestado.anexo_timbrado_s3_key)
    linhas.push({ rotulo: 'Atestado timbrado', nome: atestado.anexo_timbrado_nome || 'arquivo', tipo: 'timbrado' });
  if (atestado.cat_anexo_s3_key)
    linhas.push({ rotulo: `CAT${atestado.cat_numero ? ` (${atestado.cat_numero})` : ''}`, nome: 'arquivo CAT', tipo: 'cat' });
  if (atestado.pdf_final_s3_key)
    linhas.push({ rotulo: 'PDF gerado pelo sistema', nome: 'pdf final', tipo: 'pdf_final' });
  for (const ex of atestado.anexos_extras || []) {
    linhas.push({
      rotulo: ex.categoria === 'correcoes' ? 'Relatório de correções' : 'Arquivo base',
      nome: ex.nome, tipo: 'extra', s3_key: ex.s3_key,
    });
  }

  return (
    <div>
      {/* Dados vindos do Notion */}
      {(atestado.revisor || atestado.protocolo_crea || (atestado.tags || []).length > 0) && (
        <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {atestado.revisor && (
              <Text style={{ fontSize: 12 }}><strong>Revisor:</strong> {atestado.revisor}</Text>
            )}
            {atestado.protocolo_crea && (
              <Text style={{ fontSize: 12 }}><strong>Protocolo CREA:</strong> {atestado.protocolo_crea}</Text>
            )}
            {(atestado.tags || []).length > 0 && (
              <div>
                <Text strong style={{ fontSize: 12, marginRight: 6 }}>Tags:</Text>
                {(atestado.tags || []).map(t => (
                  <Tag key={t} color="blue" style={{ fontSize: 10, marginBottom: 4 }}>{t}</Tag>
                ))}
              </div>
            )}
          </Space>
        </Card>
      )}

      {linhas.length === 0 ? (
        <Empty description="Nenhum anexo neste atestado. Use o clipe 📎 na página de Atestados pra anexar minuta ou assinado." />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {linhas.map((l, i) => (
            <Card key={i} size="small" bodyStyle={{ padding: '8px 12px' }}>
              <Flex justify="space-between" align="center" gap={8}>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ fontSize: 12, display: 'block' }}>{l.rotulo}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{l.nome}</Text>
                </div>
                <Button size="small" type="primary" ghost
                  loading={baixando === (l.s3_key || l.tipo)}
                  onClick={() => baixar(l.tipo, l.s3_key)}>
                  Baixar
                </Button>
              </Flex>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}

export default function ModalAtestado(props: Props) {
  return <AntApp><ModalAtestadoInner {...props} /></AntApp>;
}
